import { useEffect, useRef, useState } from 'react'
import * as Constants from '../../services/Constants'
import { getDesktopSources } from '../../services/MediaDevices'
import ScreenControlDiagnosticsPageRobotjsControl from './ScreenControlDiagnosticsPageRobotjsControl'
import { sleep } from '../../utils'
import ElectronIPC from '../../services/ElectronIPC'
import { Button } from '@/UIComponents2/Button'

type DesktopCapturerSourceExt = Electron.DesktopCapturerSource & {
    id: string
    screenIndex: number
    scaleFactor: number
    screenWidth: number
    screenHeight: number
    screenPositionX: number
    screenPositionY: number
    nonScaledBounds: { x: number; y: number; width: number; height: number } | null
}

const rootContainerStyle: React.CSSProperties = {
    width: '100%',
    height: `calc(100% - ${Constants.title_bar_height})`,
    display: 'flex',
}

const preTextStyle: React.CSSProperties = {
    color: 'white',
    fontSize: 12,
    marginTop: 10,
    marginBottom: 5,
}

export default function ScreenControlDiagnosticsPage() {
    const [desktopSources, set_desktopSources] = useState<DesktopCapturerSourceExt[]>([])
    const [lastClickedPosition, _set_lastClickedPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
    const [currMousePosition, set_currMousePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
    const [currClickedCt, set_currClickedCt] = useState<number>(0)

    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const interval = setInterval(() => {
            ElectronIPC.robotJSCommand('getMousePos', []).then((pos: any) => {
                set_currMousePosition(pos)
            })
        }, 100)
        return () => {
            clearInterval(interval)
        }
    }, [])

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas || desktopSources.length === 0) {
            return
        }

        const context = canvas.getContext('2d')
        if (!context) {
            return
        }

        const canvasWidth = canvas.width
        const canvasHeight = canvas.height

        const minX = Math.min(...desktopSources.map((s) => s.screenPositionX))
        const maxX = Math.max(...desktopSources.map((s) => s.screenPositionX + s.screenWidth))
        const minY = Math.min(...desktopSources.map((s) => s.screenPositionY))
        const maxY = Math.max(...desktopSources.map((s) => s.screenPositionY + s.screenHeight))

        const boundsWidth = maxX - minX
        const boundsHeight = maxY - minY
        const boundsShiftX = minX < 0 ? 0 - minX : 0
        const boundsShiftY = minY < 0 ? 0 - minY : 0

        const scale = (() => {
            if (canvasWidth / boundsWidth < canvasHeight / boundsHeight) {
                return canvasWidth / boundsWidth // scale according to boundsWidth
            } else {
                return canvasHeight / boundsHeight // scale according to boundsHeight
            }
        })()

        // start drawing
        context.beginPath()
        context.rect(0, 0, canvasWidth, canvasHeight)
        context.lineWidth = 1
        context.strokeStyle = 'red'
        context.stroke()
        context.fillStyle = 'black'
        context.fill()

        desktopSources.forEach((screen) => {
            // Normalize screen dimensions to fit in the canvas
            const normalizedWidth = screen.screenWidth * scale
            const normalizedHeight = screen.screenHeight * scale

            // + 1 for the border
            const normalizedPositionX = (screen.screenPositionX + boundsShiftX) * scale + 1
            const normalizedPositionY = (screen.screenPositionY + boundsShiftY) * scale + 1

            context.beginPath()
            context.rect(normalizedPositionX, normalizedPositionY, normalizedWidth, normalizedHeight)
            context.lineWidth = 1
            context.strokeStyle = 'green'
            context.stroke()
            context.fillStyle = 'lightgrey'
            context.fill()
        })
    }, [desktopSources])

    useEffect(() => {
        getDesktopSources().then((list) => {
            // @ts-ignore
            set_desktopSources(list.filter((d) => d.id.startsWith('screen:')))
        })
    }, [])

    const setLastClickedPosition = (coord: { x: number; y: number }) => {
        _set_lastClickedPosition(coord)

        ElectronIPC.robotJSCommand('dragMouse', [coord.x, coord.y])
    }

    const runCursorSweep = async () => {
        const canvas = canvasRef.current
        if (!canvas || desktopSources.length === 0) {
            return
        }

        for (let d of desktopSources) {
            let bounds = d.nonScaledBounds || {
                x: d.screenPositionX,
                y: d.screenPositionY,
                width: d.screenWidth,
                height: d.screenHeight,
            }

            let topLeft = { x: bounds.x + 10, y: bounds.y + 10 }
            let topRight = { x: bounds.x + bounds.width - 10, y: bounds.y + 10 }
            let bottomLeft = { x: bounds.x + 10, y: bounds.y + bounds.height - 10 }
            let bottomRight = { x: bounds.x + bounds.width - 10, y: bounds.y + bounds.height - 10 }

            setLastClickedPosition(topLeft)
            await sleep(1000)

            setLastClickedPosition(topRight)
            await sleep(1000)

            setLastClickedPosition(bottomLeft)
            await sleep(1000)

            setLastClickedPosition(bottomRight)
            await sleep(1000)
        }
    }

    const onTestMouseClick = async () => {
        await sleep(200)
        ElectronIPC.robotJSCommand('mouseToggle', ['down', 'left'])
        await sleep(200)
        ElectronIPC.robotJSCommand('mouseToggle', ['up', 'left'])
        set_currClickedCt((c) => c + 1)
    }
    const onTestAltS = async () => {
        await sleep(3000)
        ElectronIPC.robotJSCommand('keyToggle', ['alt', 'down', ['alt']])
        await sleep(200)
        ElectronIPC.robotJSCommand('keyToggle', ['s', 'down', ['alt']])
        await sleep(500)
        ElectronIPC.robotJSCommand('keyToggle', ['s', 'up', ['alt']])
        await sleep(500)
        ElectronIPC.robotJSCommand('keyToggle', ['alt', 'up', []])
    }

    return (
        <>
            <div style={rootContainerStyle}>
                <div>
                    <pre style={preTextStyle}>Display arrangement</pre>
                    <canvas ref={canvasRef} width={400} height={250} />

                    <hr />

                    <pre style={preTextStyle}>Current mouse position</pre>
                    <pre style={preTextStyle}>{JSON.stringify(currMousePosition)}</pre>

                    <hr />

                    <pre style={preTextStyle}>
                        Mouse move test:
                        <br />
                        The mouse cursor should move to the 4 corners <br />
                        of the monitor one-by-one.
                    </pre>
                    <div style={{ marginTop: 10 }}>
                        <Button variant="rectangle" onClick={runCursorSweep}>
                            Sweep
                        </Button>
                    </div>

                    <hr />

                    <pre style={preTextStyle}>
                        Mouse move test:
                        <br />
                        The mouse cursor should move to the specified coordinate.
                    </pre>
                    <ScreenControlDiagnosticsPageRobotjsControl />

                    <hr />

                    <pre style={preTextStyle}>
                        Mouse click test:
                        <br />
                        Click the button once and do not move the mouse.
                        <br />
                        The button should keep incrementing until the mouse leaves.
                    </pre>
                    <Button variant="rectangle" onClick={onTestMouseClick}>
                        Test mouse click ({currClickedCt})
                    </Button>

                    <pre style={preTextStyle}>
                        Alt + S test:
                        <br />
                    </pre>
                    <Button variant="rectangle" onClick={onTestAltS}>
                        Test Alt + S
                    </Button>
                </div>

                <div style={{ marginLeft: 20 }}>
                    <pre style={preTextStyle}>
                        {JSON.stringify(
                            desktopSources.map((a) => ({
                                id: a.id,
                                screenIndex: a.screenIndex,
                                scaleFactor: a.scaleFactor,
                                screenWidth: a.screenWidth,
                                screenHeight: a.screenHeight,
                                screenPositionX: a.screenPositionX,
                                screenPositionY: a.screenPositionY,
                                // @ts-ignore
                                nonScaledBounds: a.nonScaledBounds,
                            })),
                            null,
                            '    '
                        )}
                    </pre>
                </div>
            </div>
        </>
    )
}
